// CreateUserContext.tsx
import React, { createContext, useState, useContext, ReactNode } from 'react';

export interface CreateUserData {
  // Step 1 - Basic info
  userId: string;
  userName: string;
  email: string;
  mobileNo: string;
  department: string;
  departmentName: string;

  // Step 1 - Password fields (new)
  password: string;
  confirmPassword: string;

  // Step 2 - Contact details
  address: string;
  street: string;
  city: string;
  state: string;
  country: string;

  // Step 3 - Role and shift
  role: string;
  roleName: string;
  shift: string;
}

interface CreateUserContextType {
  createUserData: CreateUserData;
  setCreateUserData: React.Dispatch<React.SetStateAction<CreateUserData>>;
  resetCreateUserData: () => void;
}

const CreateUserContext = createContext<CreateUserContextType | undefined>(undefined);

export const CreateUserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getInitialState = (): CreateUserData => ({
    userId: '#U0025',
    userName: '',
    email: '',
    mobileNo: '',
    department: '',
    departmentName: '',

    // ✅ Initialize password fields
    password: '',
    confirmPassword: '',

    address: '',
    street: '',
    city: '',
    state: '',
    country: '',
    role: '',
    roleName: '',
    shift: '',
  });

  const [createUserData, setCreateUserData] = useState<CreateUserData>(getInitialState());

  const resetCreateUserData = () => setCreateUserData(getInitialState());

  return (
    <CreateUserContext.Provider value={{ createUserData, setCreateUserData, resetCreateUserData }}>{children}</CreateUserContext.Provider>
  );
};

export const useCreateUser = (): CreateUserContextType => {
  const context = useContext(CreateUserContext);
  if (!context) throw new Error('useCreateUser must be used within CreateUserProvider');
  return context;
};
